<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import AppLoader from '../../components/AppLoader.vue';
import AppHeader from '../../components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import ConnectorTab from '../../components/ConnectorTab.vue';
import ErrorMessage from '../../components/ErrorMessage.vue';

import { useTransformsStore } from '../../stores/transforms.store';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import _ from 'lodash';
import RowActionMenu from '@/components/RowActionMenu.vue';
import StatCard from '@/components/global/StatCard.vue';
import HighChart from '../../components/HighChart.vue';

import { ChevronRightIcon, CheckCircleIcon, CpuChipIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/outline';
import { QueueListIcon } from '@heroicons/vue/24/solid';

import useConfirmBeforeAction from '../../composables/useConfirmBeforeAction.js';
import TransformImplementationPage from './TransformImplementationPage.vue';

import { formatter, getMetricFormattingInfo, getStatusColor } from '../../utils/utils';
import Tabs from '@/components/global/Tabs.vue';
import { getTransformIcon } from '@/utils/constants';
const props = defineProps(['id', 'customMessage', 'source', 'destination']);
const transformsStore = useTransformsStore();

let transform = ref(null);
let router = useRouter();
let route = useRoute();

let generalLoading = ref(false);
let actionLoading = ref(false);
let configurationLoading = ref(false);
let tabLoading = ref(false);
const emit = defineEmits(['toggle-mobile-sidebar']);

const chart = ref(null);
const showChart = ref(true);
const hidePromptTemporary = ref(false);
let currentTab = ref('');
let hideTabs = ref(false);

let canSave = ref(false);

const selectedChart = ref('throughput');
const chartVariants = ref([
    {
        id: 'throughput',
        metricName: 'Throughput',
        chartName: 'Throughput Over Time',
        unit: 'GB',
        data: [
            150.0, 200.5, 250.4, 300.2, 350.0, 400.0, 450.6, 500.5, 550.4, 600.1, 650.6, 700.4, 550.0, 800.5, 250.4, 300.2, 350.0, 400.0, 450.6,
            500.5, 550.4, 600.1, 650.6, 700.4
        ], // Mock data
        color: '#958cee'
    },
    {
        id: 'lag',
        metricName: 'Lag',
        chartName: 'Lag Over Time',
        unit: 's',
        data: [0.1, 0.5, 0.4, 0.2, 0.0, 0.0, 0.6, 0.5, 0.4, 0.1, 0.6, 0.4, 0.0, 0.5, 0.4, 0.2, 0.0, 0.0, 0.6, 0.5, 0.4, 0.1, 0.6, 0.4], // Mock data
        color: '#ee8c8c'
    },
    {
        id: 'latency',
        metricName: 'Latency',
        chartName: 'Latency Over Time',
        unit: 'ms',
        data: [120, 85, 600, 300, 150, 200, 250, 400, 450, 500, 550, 600, 650, 700, 550, 800, 250, 300, 350, 400, 450, 500, 550, 600], // Mock data
        color: '#8cadee'
    }
]);

const chartOptions = ref(null);

let metricsLoading = ref(false);
let defaultTransform = ref('');

let tabs = [
    { name: 'Status', href: '#', current: true },
    { name: 'Settings', href: '#', current: false },
    { name: 'Implementation', href: '#', current: true }
];

const setCurrentTab = (str) => {
    if (str === 'Source' || str === 'Destination') {
        return;
    }

    currentTab.value = str;

    router.push({
        query: { tab: currentTab.value, source: route.query.source, destination: route.query.destination, customMessage: route.query.customMessage }
    });
};

const toggleMobileSidebar = (value) => {
    emit('toggle-mobile-sidebar', value);
};

const transformConfig = computed(() => {
    if (transformsStore.configs) {
        console.log(transformsStore.configs, transform.value.transform);
        const found = Object.values(transformsStore.configs).find(
            (x) => x.filter((y) => (y.transform || y.transform_type) === (transform.value.transform || transform.value.transform_type)).length
        );

        return found?.[0]?.config;
    }
});

const transformTypeDisplayName = computed(() => {
    if (transformsStore.configs) {
        console.log(transformsStore.configs, transform.value.transform);
        const found = Object.values(transformsStore.configs).find(
            (x) => x.filter((y) => (y.transform || y.transform_type) === (transform.value.transform || transform.value.transform_type)).length
        );

        return found?.[0]?.display_name;
    }
});

// watch route and if there is no query string called tab and with the valuE imPLEMENTATION, show the tabs and
watch(
    () => route.query,
    (newValue) => {
        if (newValue?.tab === 'Implementation') {
            toggleMobileSidebar(true);

            hideTabs.value = true;
        } else {
            toggleMobileSidebar(false);
            hideTabs.value = false;
            setCurrentTab(newValue?.tab || 'Status');
        }
    },
    { immediate: true }
);

const getFormattedValue = (metricKey, value) => {
    const found = Object.values(transformsStore.configs).find(
        (x) => x.filter((y) => (y.transform || y.transform_type) === (transform.value.transform || transform.value.transform_type)).length
    );

    const metricInfo = getMetricFormattingInfo(metricKey, found?.[0]?.metrics);

    return formatter(value, metricInfo || {});
};

const stats = computed(() => {
    return [
        {
            id: 0,
            name: 'Status',
            stat: getFormattedValue('status', transform.value?.metrics?.status).display,
            icon: CheckCircleIcon,
            colorClass: getStatusColor('active', false, true)
        },
        {
            id: 2,
            name: 'Tasks',
            stat: getFormattedValue('tasks', transform.value?.metrics?.tasks).display,
            icon: QueueListIcon,
            colorClass: 'bg-green-400'
        },
        {
            id: 4,
            name: 'Latency',
            icon: ClockIcon,
            colorClass: 'bg-yellow-500',
            stat: getFormattedValue('latency', transform.value?.metrics?.latency).display
        },
        {
            id: 4,
            name: 'Duration',
            stat: getFormattedValue('duration', transform.value?.metrics?.duration).display,
            icon: CpuChipIcon,
            colorClass: 'bg-purple-400'
        }
    ];
});

onMounted(async () => {
    generalLoading.value = true;
    try {
        await Promise.all([
            transformsStore.getById(route.params.id).then((data) => {
                transform.value = data;
                defaultTransform.value = _.cloneDeep(data);

                console.log(transform.value, defaultTransform.value);
            }),
            transformsStore.fetchTransformConfigs().then(() => {
                configurationLoading.value = false;
            })
        ]);
    } finally {
        generalLoading.value = false;
    }

    setCurrentTab(route.query.tab || 'Status');

    if (route.query.tab === 'Implementation') {
        toggleMobileSidebar(true);
        hideTabs.value = true;
    }

    onChartChange();
});

const onChartChange = () => {
    const option = _.cloneDeep(chartVariants.value).find((x) => x.id === selectedChart.value);
    const chartRef = chart.value?.chart?.chart;

    const options = {
        chart: {
            type: 'column',
            height: '300px'
        },
        title: {
            text: option.chartName
        },
        xAxis: {
            categories: [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec'
            ]
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Throughput'
            }
        },
        tooltip: {
            headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
            pointFormat: '<tr><td style="padding:0"><b>{point.y:.1f} units</b></td></tr>',
            footerFormat: '</table>',
            shared: true,
            useHTML: true
        },
        plotOptions: {
            column: {
                pointPadding: 0.2,
                borderWidth: 0,
                dataLabels: {
                    enabled: true
                }
            }
        },
        series: [
            {
                name: `${option.metricName} (${option.unit})`,
                data: option.data, // Mock data
                color: option.color,
                borderRadiusTopLeft: '15%',
                borderRadiusTopRight: '15%'
            }
        ]
    };

    if (chartRef) {
        chartOptions.value = options;
        chartRef.update(options);
    } else {
        chartOptions.value = options;
    }
};

const onSave = async () => {
    try {
        actionLoading.value = true;

        await transformsStore.editTransform(transform.value);

        transform.value = transformsStore.all.find((x) => x.id === transform.value?.id);
        defaultTransform.value = _.cloneDeep(transform.value);
        canSave.value = false;
    } catch (error) {
        console.log(error);
    } finally {
        actionLoading.value = false;
    }
};

function onDiscard() {
    transform.value = _.cloneDeep(defaultTransform.value);
    canSave.value = false;
}

async function onDelete(name, id) {
    useConfirmBeforeAction(
        'delete',
        async () => {
            try {
                tabLoading.value = true;
                actionLoading.value = true;
                hidePromptTemporary.value = true;
                await transformsStore.delete(id);

                router.push({ name: 'transforms' });
            } finally {
                tabLoading.value = false;
                actionLoading.value = false;

                hidePromptTemporary.value = false;
            }
        },
        { entity: 'transform', name: name },
        () => {
            tabLoading.value = false;
            actionLoading.value = false;
        }
    );
}

function onClone() {}

function onEditImplementation() {
    router.push({ name: 'transform-implementation', params: { id: transform.value.id } });
}

onBeforeRouteLeave((to, from, next) => {
    if (generalLoading.value || actionLoading.value || metricsLoading.value) {
        hideTabs.value = false;
        toggleMobileSidebar(false);
        return next();
    }

    if (hidePromptTemporary.value || !!transform.value?.name) {
        hideTabs.value = false;
        toggleMobileSidebar(false);
        return next();
    }

    const answer = window.confirm('Are you sure you want to leave? You have unsaved changes.');

    if (answer) {
        hideTabs.value = false;
        toggleMobileSidebar(false);
        return next();
    } else {
        return next(false);
    }
});
</script>

<template>
    <div class="flex flex-col overflow-hidden" :class="hideTabs ? 'h-[calc(100vh-4.5rem)]' : 'h-[calc(100vh-1rem)]'">
        <div class="page-header" :class="hideTabs ? 'absolute left-10 top-2 w-full !w-[calc(100vw-4rem)]' : ''">
            <AppHeader :show-title="false" class="w-full">
                <template #titleRight>
                    <nav class="text-gray-700" aria-label="Breadcrumb">
                        <ol class="list-none p-0 inline-flex font-semibold">
                            <li class="flex items-center">
                                <router-link to="/transforms" class="text-green-500 hover:text-cyan-900">Transforms</router-link>
                                <ChevronRightIcon class="w-4 h-4 mx-2 text-cyan-950" />
                                <div class="flex flex-row align-center items-center gap-3">
                                    <div class="flex flex-row align-center items-center gap-3">
                                        <div v-if="transform?.transform_type" class="flex-shrink-0">
                                            <img class="h-6 w-6" :src="getTransformIcon(transform.transform_type)" alt="transform icon" />
                                        </div>
                                        <h3
                                            class="text-base font-semibold text-cyan-950 flex flex-col items-left truncate"
                                            style="max-width: 25rem"
                                            :title="transform?.name">
                                            <router-link
                                                v-if="transform?.id && hideTabs"
                                                :to="`/transforms/view/${transform?.id}`"
                                                class="text-green-500 hover:text-cyan-900">
                                                {{ transform?.name }}
                                            </router-link>
                                            <span v-if="!hideTabs">{{ transform?.id ? transform?.name : 'Loading...' }}</span>
                                            <span v-if="transform?.id" class="text-xs text-gray-500">ID: {{ transform?.id }}</span>
                                        </h3>
                                    </div>
                                </div>
                                <ChevronRightIcon v-if="hideTabs" class="w-4 h-4 mx-2 text-cyan-950" />
                                <span v-if="hideTabs" class="text-cyan-950">Implementation</span>
                            </li>
                        </ol>
                    </nav>
                </template>
                <template #end>
                    <RowActionMenu
                        v-if="!generalLoading"
                        :disabled="generalLoading || actionLoading || tabLoading"
                        :show-delete="true"
                        :show-clone="false"
                        @delete="onDelete(transform.name, transform.id)"
                        @clone="onClone"
                        :loading="actionLoading || tabLoading || tabLoading" />
                </template>
            </AppHeader>
        </div>
        <div class="flex-1 overflow-auto">
            <AppLoader :force-loading="generalLoading" default-height="75vh">
                <div v-if="transform" class="w-full">
                    <Tabs
                        :tabs="tabs"
                        :initial-active-tab="currentTab"
                        :loading="tabLoading || generalLoading"
                        :hide-tabs="hideTabs"
                        @tab-change="setCurrentTab">
                        <template #Status>
                            <div class="mt-5">
                                <div class="w-full mt-3">
                                    <h3 class="mb-3 font-medium">Stats</h3>

                                    <div class="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                                        <StatCard
                                            v-for="item in stats"
                                            :key="item.id"
                                            :icon-bg-class="item.colorClass"
                                            :info-icon="item.infoIcon"
                                            :errors="item.errors"
                                            :icon="item.icon"
                                            :loading="metricsLoading || configurationLoading"
                                            :title="item.name"
                                            :value="item.stat" />
                                    </div>
                                </div>
                                <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 p-5 rounded-lg shadow-md space-y-3 bg-white mt-10">
                                    <div class="space-y-8 border-cyan-950/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-cyan-950/10 sm:pb-0">
                                        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                            <label for="first-name" class="block text-sm font-semibold text-cyan-950 sm:pt-1.5 flex items-center"
                                                >Transform Name</label
                                            >
                                            <div class="mt-2 sm:col-span-2 sm:mt-0">
                                                <span class="text-sm text-gray-500">{{ transform.name }}</span>
                                            </div>
                                        </div>

                                        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                            <label for="first-name" class="block text-sm font-semibold text-cyan-950 sm:pt-1.5 flex items-center"
                                                >Type</label
                                            >
                                            <div class="mt-2 sm:col-span-2 sm:mt-0">
                                                <div class="flex items-center gap-1 text-sm">
                                                    <img
                                                        v-if="transform.transform_type"
                                                        width="20"
                                                        height="20"
                                                        class="mr-1"
                                                        :src="getTransformIcon(transform.transform_type)"
                                                        alt="transform icon" />
                                                    <span class="text-sm text-gray-500">{{ transformTypeDisplayName }}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                            <label for="first-name" class="block text-sm font-semibold text-cyan-950 sm:pt-1.5 flex items-center"
                                                >Job Name</label
                                            >
                                            <div class="mt-2 sm:col-span-2 sm:mt-0">
                                                <span class="text-sm text-gray-500">{{ transform.job_name }}</span>
                                            </div>
                                        </div>

                                        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                            <label for="first-name" class="block text-sm font-semibold text-cyan-950 sm:pt-1.5 flex items-center"
                                                >Topic</label
                                            >
                                            <div class="mt-2 sm:col-span-2 sm:mt-0">
                                                <div class="flex flex-col gap-1">
                                                    <span class="text-sm text-gray-500"><b>Name:</b> {{ transform.topic }}</span>
                                                    <span class="text-sm text-gray-500"><b>ID:</b> {{ transform.topic_id }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- <div class="bg-white p-4 rounded-lg shadow my-5">
                                    <div class="flex items-center justify-between">
                                        <div class="flex gap-2 items-center w-full">
                                            <ChevronUpIcon
                                                v-if="showChart"
                                                class="h-5 w-5 text-gray-600 cursor-pointer hover:text-gray-800"
                                                @click="showChart = !showChart" />
                                            <ChevronDownIcon
                                                v-else
                                                class="h-5 w-5 text-gray-600 cursor-pointer hover:text-gray-800"
                                                @click="showChart = !showChart" />
                                            <h3 v-if="!showChart">{{ chartOptions.title.text }}</h3>
                                        </div>
                                        <div class="w-1/3">
                                            <div class="relative">
                                                <select v-model="selectedChart" class="form-select text-sm" @change="onChartChange">
                                                    <option value="" disabled>Select chart</option>
                                                    <option v-for="variant in chartVariants" :key="variant.id" :value="variant.id">
                                                        {{ variant.metricName }}
                                                    </option>
                                                </select>
                                                <div class="select-icon">
                                                    <ChevronDownIcon class="h-5 w-5 text-gray-600" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <HighChart v-if="showChart" ref="chart" :chart-options="chartOptions" />
                                </div> -->
                            </div>
                        </template>

                        <template #Settings>
                            <div class="mt-5 w-full">
                                <AppLoader :force-loading="configurationLoading" default-height="20vh">
                                    <ConnectorTab
                                        v-if="transform.config && transformConfig"
                                        :key="transform.config"
                                        :connector="transform"
                                        :default-connector="defaultTransform"
                                        :config="transformConfig"
                                        :justify-content="'start'"
                                        :entity-type="'transform'"
                                        :tab="'settings'"
                                        :edit-mode="true"
                                        @changed="(status) => (canSave = status)" />

                                    <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'edit-destination'" />
                                </AppLoader>
                            </div>
                        </template>

                        <template #Implementation>
                            <TransformImplementationPage :transform="transform" @toggle-mobile-sidebar="toggleMobileSidebar" />
                        </template>
                    </Tabs>
                </div>
            </AppLoader>
        </div>

        <div class="mt-auto">
            <AppFooter v-if="transform && !hideTabs">
                <template #right>
                    <RowActionMenu
                        v-if="!generalLoading"
                        :show-save="true"
                        :disable-save="!canSave"
                        :show-discard="false"
                        :loading="actionLoading || tabLoading || generalLoading"
                        :disabled="generalLoading || actionLoading"
                        @save="onSave()"
                        @discard="onDiscard" />
                </template>
            </AppFooter>
        </div>
    </div>
</template>

<style></style>
